import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  type DocumentSnapshot,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import {
  callClaimCoke, callDealNextHand, callNudgeIdleTurn, callStartRematch,
  callSubmitBid, callSubmitPlay, callSubmitTrumpChoice,
} from "../lib/functions";
import {
  BidPill, DealAnimation, FourSeatTrickLayout, HandFan, HandResultCenter, ScoreBadge,
  SUIT_LABEL, SUIT_SYMBOL, TRUMP_CHOICE_BUTTON_CLASS, TrickPlaysAtSeats, TrumpSuitHero, TurnChip,
} from "../components/cards";
import {
  cardsPlayedFromLogForSeat,
  isCokeHand,
  legalPlays,
  projectedRunningTotals,
} from "../engine/engine";
import type {
  Bid, BidValue, Card, GameEvent, HandScore, HandState, Phase, Ruleset, SeatIndex, Suit,
  TeamId, Trick,
} from "../types/game";
import { DEFAULT_RULESET, TEAM_OF } from "../types/game";
import type { RoomDoc } from "../types/room";
import { RULESET_INFO } from "../content/rules";

// Shapes that mirror functions/src/types.ts on the server side.
interface SeatOccupant {
  uid?: string;
  screenName: string;
  avatarUrl?: string | null;
  botDifficulty?: "advanced" | "expert";
}
interface PublicHandState {
  dealer: SeatIndex;
  phase: Phase;
  turn?: SeatIndex;
  bidder?: SeatIndex;
  bid?: BidValue;
  trump?: Suit;
  bids: Bid[];
  tricks: Trick[];
  currentTrick?: Trick;
  handSizes: [number, number, number, number];
  score?: HandScore;
  cokeClaimed?: SeatIndex[];
  abandoned?: boolean;
}
interface MatchDoc {
  roomId: string;
  playerUids: string[];
  seats: (SeatOccupant | null)[];
  handNumber: number;
  scoreA: number;
  scoreB: number;
  winner?: TeamId;
  public: PublicHandState;
  eventLog: GameEvent[];
  lastActionAt?: Timestamp;
  // Matches created before PR 1 lack this field; default to "tpc" at read time.
  ruleset?: Ruleset;
}

// Time the 4 cards sit in the center before they fly off to the winner.
const TRICK_FREEZE_MS = 1500;
const TRICK_FREEZE_LAST_MS = 2600;
// Extra linger for the fly-out animation.
const TRICK_EXIT_MS = 700;
// Keep in sync with functions/src/matches.ts (IDLE_TURN_TIMEOUT_MS).
const IDLE_TURN_TIMEOUT_MS = 30_000;
const REACTION_COOLDOWN_MS = 3_000;
const REACTION_VISIBLE_MS = 3_000;
const REACTION_FADE_MS = 600;
const REACTION_EMOJIS = ["😂", "😤", "🤔", "👏", "🔥", "😬", "💀", "🃏"] as const;

type ReactionEmoji = (typeof REACTION_EMOJIS)[number];
type SeatReaction = { id: string; emoji: ReactionEmoji; fading: boolean };

function reactionCreatedAtMs(raw: unknown): number {
  if (raw instanceof Timestamp) return raw.toMillis();
  if (raw != null && typeof raw === "object" && typeof (raw as Timestamp).toMillis === "function") {
    try {
      return (raw as Timestamp).toMillis();
    } catch {
      /* ignore */
    }
  }
  // Firestore occasionally surfaces plain protobuf-shaped objects instead of Timestamp.
  if (raw != null && typeof raw === "object") {
    const o = raw as { seconds?: unknown; nanoseconds?: unknown };
    if (typeof o.seconds === "number") {
      const ns = typeof o.nanoseconds === "number" ? o.nanoseconds : 0;
      return o.seconds * 1000 + Math.floor(ns / 1e6);
    }
  }
  return Date.now();
}

function coerceSeat(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "bigint") return Number(raw);
  if (typeof raw === "string") {
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
}

function coerceReactionEmoji(raw: unknown): ReactionEmoji | null {
  if (typeof raw !== "string") return null;
  const n = raw.normalize("NFC").trim();
  return REACTION_EMOJIS.includes(n as ReactionEmoji) ? (n as ReactionEmoji) : null;
}

function perfDebugEnabled(): boolean {
  try {
    return localStorage.getItem("setback-debug-perf") === "1";
  } catch {
    return false;
  }
}

/** Console: `[setback][match] snapshot` whenever the match doc changes. Set localStorage setback-debug-match=1 */
function matchFirestoreDebugEnabled(): boolean {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem("setback-debug-match") === "1";
  } catch {
    return false;
  }
}

export default function OnlinePlayPage() {
  const { matchId } = useParams<{ matchId: string }>();
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const [match, setMatch] = useState<MatchDoc | null>(null);
  const [myCards, setMyCards] = useState<Card[] | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rematchBusy, setRematchBusy] = useState(false);
  /** Lobby doc for this table — host check + follow room.matchId after rematch. */
  const [tableRoom, setTableRoom] = useState<RoomDoc | undefined>(undefined);
  const [seatReactions, setSeatReactions] = useState<Partial<Record<SeatIndex, SeatReaction>>>({});
  const [nextReactionAt, setNextReactionAt] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const reactionTimersRef = useRef<Map<string, number[]>>(new Map());
  /** First snapshot IDs are suppressed so historical reactions don't all pop at once. */
  const reactionsBootstrappedRef = useRef(false);
  const reactionSeenDocIdsRef = useRef<Set<string>>(new Set());
  const handStartMsRef = useRef<number | null>(null);
  const loggedReadyHandRef = useRef<number | null>(null);
  /** Hand boundary for clearing stale private cards before the new snapshot lands. */
  const prevHydrateHandNumRef = useRef<number | null>(null);
  /** Mirrors `handNumber` on `/privateHands/{uid}` so we don't wipe cards after a race (private snapshot arrives before match). */
  const lastPrivateDocHandNumberRef = useRef<number | null>(null);
  const matchForPerfRef = useRef<MatchDoc | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, []);

  // Subscribe to the public match doc. Note: the match doc is written shortly
  // after the room flips to "playing", so the first snapshot may arrive empty
  // for a brief moment. We give it a few seconds before declaring "not found".
  useEffect(() => {
    if (!matchId) return;
    let notFoundTimer: ReturnType<typeof setTimeout> | null = null;
    const unsub = onSnapshot(
      doc(db, "matches", matchId),
      snap => {
        if (snap.exists()) {
          if (notFoundTimer) { clearTimeout(notFoundTimer); notFoundTimer = null; }
          setNotFound(false);
          const md = snap.data() as MatchDoc;
          if (matchFirestoreDebugEnabled()) {
            console.debug("[setback][match] snapshot", matchId, {
              winner: md.winner ?? null,
              phase: md.public?.phase,
              scoreA: md.scoreA,
              scoreB: md.scoreB,
              handNumber: md.handNumber,
              abandoned: md.public?.abandoned,
              fromCache: snap.metadata.fromCache,
            });
          }
          setMatch(md);
          return;
        }
        if (!notFoundTimer) {
          notFoundTimer = setTimeout(() => setNotFound(true), 5000);
        }
      },
      err => {
        console.error("match snapshot error", err);
        setError(`${err.message} (${err.code ?? "unknown"})`);
      },
    );
    return () => {
      if (notFoundTimer) clearTimeout(notFoundTimer);
      unsub();
    };
  }, [matchId]);

  // Same room the match was created from — host can rematch; everyone follows new matchId.
  useEffect(() => {
    if (!match?.roomId || !user) return;
    const unsub = onSnapshot(
      doc(db, "rooms", match.roomId),
      snap => setTableRoom(snap.exists() ? { ...(snap.data() as RoomDoc) } : undefined),
      err => {
        console.error("room snapshot error", err);
        setTableRoom(undefined);
      },
    );
    return unsub;
  }, [match?.roomId, user?.uid]);

  useEffect(() => {
    if (!tableRoom || !matchId) return;
    const nextMatchId =
      tableRoom.rematchMatchId ??
      (tableRoom.status === "playing" ? tableRoom.matchId : undefined);
    if (nextMatchId && nextMatchId !== matchId) {
      navigate(`/play-online/${nextMatchId}`, { replace: true });
    }
  }, [tableRoom, matchId, navigate]);

  useEffect(() => {
    matchForPerfRef.current = match;
  }, [match]);

  useEffect(() => {
    prevHydrateHandNumRef.current = null;
    lastPrivateDocHandNumberRef.current = null;
    setMyCards(null);
  }, [matchId]);

  // Subscribe to our own private hand (same doc path for the whole match — do not re-subscribe
  // on every handNumber or we pay an extra round-trip / listener cold-start right when speed matters).
  useEffect(() => {
    if (!matchId || !user) return;
    const unsub = onSnapshot(
      doc(db, "matches", matchId, "privateHands", user.uid),
      snap => {
        if (!snap.exists()) {
          setMyCards(null);
          return;
        }
        const row = snap.data() as { cards: Card[]; handNumber?: number };
        const cards = row.cards ?? [];
        const docHn =
          typeof row.handNumber === "number"
            ? row.handNumber
            : matchForPerfRef.current?.handNumber;
        if (typeof docHn === "number") {
          lastPrivateDocHandNumberRef.current = docHn;
        }
        setMyCards(cards);
        if (perfDebugEnabled()) {
          const handStartMs = handStartMsRef.current;
          const deltaMs = handStartMs == null ? null : Math.round(performance.now() - handStartMs);
          console.debug("[perf][deal] private hand snapshot arrived", {
            matchId,
            handNumber: matchForPerfRef.current?.handNumber,
            cards: cards.length,
            msSinceHandStart: deltaMs,
          });
        }
      },
      err => {
        console.error("private hand snapshot error", err);
        setError(`${err.message} (${err.code ?? "unknown"})`);
      },
    );
    return unsub;
  }, [matchId, user?.uid]);

  // Trick freeze (same idea as local PlayPage): two phases — cards sit
  // with winner ringed, then fly toward the winning seat.
  const [frozenTrick, setFrozenTrick] = useState<Trick | null>(null);
  const [trickCardsVisible, setTrickCardsVisible] = useState(true);
  const prevTricksRef = useRef<{ handNum: number; count: number }>({ handNum: 0, count: 0 });
  useLayoutEffect(() => {
    if (!match) return;
    const pub = match.public;
    const prev = prevTricksRef.current;
    const cur = { handNum: match.handNumber, count: pub.tricks.length };
    const handChanged = cur.handNum !== prev.handNum;
    const justFinished = !handChanged && cur.count > prev.count;
    prevTricksRef.current = cur;
    if (handChanged) {
      setFrozenTrick(null);
      setTrickCardsVisible(true);
      return;
    }
    if (justFinished) {
      setFrozenTrick(pub.tricks[pub.tricks.length - 1]);
      setTrickCardsVisible(true);
    }
  }, [match?.handNumber, match?.public.tricks.length]);

  useEffect(() => {
    if (!match || !frozenTrick) return;
    const n = match.public.tricks.length;
    const isLastTrick = n === 6;
    const freezeMs = isLastTrick ? TRICK_FREEZE_LAST_MS : TRICK_FREEZE_MS;
    const t1 = window.setTimeout(() => setTrickCardsVisible(false), freezeMs);
    const t2 = window.setTimeout(() => {
      setFrozenTrick(null);
      setTrickCardsVisible(true);
    }, freezeMs + TRICK_EXIT_MS);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [match?.handNumber, frozenTrick]);

  // ---- Idle-turn auto-bot ----------------------------------
  // If it's a human's turn other than ours, and they've been idle for more
  // than IDLE_TURN_TIMEOUT_MS, nudge the server to play a bot move on their
  // behalf. Any seated human can nudge; the server is idempotent.
  const [nudgeStatus, setNudgeStatus] = useState<string | null>(null);
  useEffect(() => {
    if (!matchId || !match || !user || match.winner) return;
    const actor = currentActor(match.public);
    if (actor === null) return;
    const occupant = match.seats[actor];
    if (!occupant?.uid) return;              // bot — handled by server trigger
    if (occupant.uid === user.uid) return;   // our own turn — do nothing

    const lastMs = match.lastActionAt?.toMillis?.() ?? null;
    if (lastMs === null) return;
    const age = Date.now() - lastMs;
    const delay = Math.max(0, IDLE_TURN_TIMEOUT_MS - age);

    let cancelled = false;
    const t = setTimeout(async () => {
      if (cancelled) return;
      try {
        setNudgeStatus("Player was idle — bot stepping in…");
        await callNudgeIdleTurn(matchId);
      } catch (err) {
        console.error("nudgeIdleTurn failed", err);
      } finally {
        if (!cancelled) setTimeout(() => setNudgeStatus(null), 2500);
      }
    }, delay + 500);  // tiny buffer to avoid a clock-skew false nudge

    return () => { cancelled = true; clearTimeout(t); };
  }, [matchId, match?.handNumber, match?.public.phase, match?.public.turn,
      match?.public.bidder, match?.lastActionAt?.toMillis?.(),
      match?.winner, user?.uid]);

  // ---- Derived state ---------------------------------------
  const mySeat: SeatIndex | null = useMemo(() => {
    if (!match || !user) return null;
    const idx = match.seats.findIndex(s => s?.uid === user.uid);
    return idx === -1 ? null : (idx as SeatIndex);
  }, [match, user?.uid]);

  const legalMyPlays = useMemo<Card[]>(() => {
    if (!match || mySeat === null || !myCards) return [];
    if (match.public.phase !== "playing" || match.public.turn !== mySeat) return [];
    const hs = reconstructHandState(match, mySeat, myCards);
    return legalPlays(hs, mySeat, match.ruleset ?? DEFAULT_RULESET);
  }, [match, mySeat, myCards]);

  // Deal animation: fires once when the server flips into a new fresh-
  // bidding hand (handNumber changed, no bids or tricks yet). Keep this
  // non-blocking so cards/actions appear as soon as Firestore snapshots land.
  const lastDealtHandRef = useRef<number>(-1);
  const [dealingForHand, setDealingForHand] = useState<number | null>(null);

  // Fullscreen deal overlay is ~1s+ of motion; lift it as soon as this client’s private hand
  // matches the public hand sizes for the hand we’re animating — game is playable immediately.
  useEffect(() => {
    if (dealingForHand === null || !match || mySeat === null || myCards === null) return;
    if (dealingForHand !== match.handNumber) return;
    const expected = match.public.handSizes[mySeat];
    if (expected <= 0 || myCards.length !== expected) return;
    setDealingForHand(null);
  }, [dealingForHand, match, mySeat, myCards]);

  useEffect(() => {
    if (!match || myCards == null || mySeat === null) return;
    if (loggedReadyHandRef.current === match.handNumber) return;
    const expected = match.public.handSizes[mySeat];
    if (expected <= 0 || myCards.length !== expected) return;
    const handStartMs = handStartMsRef.current;
    const deltaMs = handStartMs == null ? null : Math.round(performance.now() - handStartMs);
    if (perfDebugEnabled()) {
      console.debug("[perf][deal] hand render ready", {
        matchId,
        handNumber: match.handNumber,
        expected,
        actual: myCards.length,
        msSinceHandStart: deltaMs,
      });
    }
    loggedReadyHandRef.current = match.handNumber;
  }, [matchId, match, myCards, mySeat]);

  useEffect(() => {
    if (!matchId) return;
    reactionsBootstrappedRef.current = false;
    reactionSeenDocIdsRef.current = new Set();
    const reactionsCol = collection(db, "matches", matchId, "reactions");
    const rejectReactionId = (id: string) => {
      reactionSeenDocIdsRef.current.add(id);
    };

    const applyReactionDoc = (docSnap: DocumentSnapshot) => {
      const id = docSnap.id;
      if (reactionSeenDocIdsRef.current.has(id)) return;
      if (!docSnap.exists()) return;
      const data = docSnap.data() as {
        uid?: string;
        seat?: unknown;
        emoji?: unknown;
        createdAt?: unknown;
      };
      const seatNum = coerceSeat(data.seat);
      const emojiRaw = coerceReactionEmoji(data.emoji);
      console.log("[reactions] apply doc", id, {
        seatRaw: data.seat,
        seatCoerced: seatNum,
        emojiRaw: data.emoji,
        emojiCoerced: emojiRaw,
      });
      if (![0, 1, 2, 3].includes(seatNum)) {
        console.warn("[reactions] skip: invalid seat", id);
        rejectReactionId(id);
        return;
      }
      if (!emojiRaw) {
        console.warn("[reactions] skip: emoji not in allow-list", id, data.emoji);
        rejectReactionId(id);
        return;
      }
      const seat = seatNum as SeatIndex;
      const emoji = emojiRaw;
      const createdMs = reactionCreatedAtMs(data.createdAt);
      if (Number.isFinite(createdMs) && Date.now() - createdMs > REACTION_VISIBLE_MS + 1000) {
        console.warn("[reactions] skip: stale", id, { ageMs: Date.now() - createdMs });
        rejectReactionId(id);
        return;
      }

      reactionSeenDocIdsRef.current.add(id);
      const priorTimers = reactionTimersRef.current.get(id);
      if (priorTimers) for (const t of priorTimers) window.clearTimeout(t);

      console.log("[reactions] (2→state) animate seat", seat, emoji, "docId", id);
      setSeatReactions(prev => ({ ...prev, [seat]: { id, emoji, fading: false } }));

      const fadeTimer = window.setTimeout(() => {
        setSeatReactions(prev => {
          if (prev[seat]?.id !== id) return prev;
          return { ...prev, [seat]: { ...prev[seat]!, fading: true } };
        });
      }, Math.max(0, REACTION_VISIBLE_MS - REACTION_FADE_MS));

      const removeTimer = window.setTimeout(() => {
        setSeatReactions(prev => {
          if (prev[seat]?.id !== id) return prev;
          const next = { ...prev };
          delete next[seat];
          return next;
        });
        reactionTimersRef.current.delete(id);
      }, REACTION_VISIBLE_MS);

      reactionTimersRef.current.set(id, [fadeTimer, removeTimer]);
    };

    const unsub = onSnapshot(
      reactionsCol,
      snap => {
        const changes = snap.docChanges();
        const firstBootstrap = !reactionsBootstrappedRef.current;
        console.log("[reactions] (2) snapshot", {
          matchId,
          bootstrap: firstBootstrap,
          docs: snap.size,
          changes: changes.map(c => `${c.type}:${c.doc.id}`),
          fromCache: snap.metadata.fromCache,
        });

        // First hydrate: suppress old backlog; only animate docs that aren't obviously stale yet.
        if (!reactionsBootstrappedRef.current) {
          reactionsBootstrappedRef.current = true;
          const now = Date.now();
          for (const d of snap.docs) {
            const raw = d.data() as { createdAt?: unknown };
            const createdMs = reactionCreatedAtMs(raw.createdAt);
            const isStale =
              Number.isFinite(createdMs) && now - createdMs > REACTION_VISIBLE_MS + 1000;
            if (isStale) reactionSeenDocIdsRef.current.add(d.id);
          }
          for (const change of changes) {
            if (change.type === "removed") continue;
            if (change.type === "added" || change.type === "modified") {
              applyReactionDoc(change.doc);
            }
          }
          for (const d of snap.docs) applyReactionDoc(d);
          return;
        }

        for (const change of changes) {
          if (change.type === "removed") continue;
          if (change.type === "added" || change.type === "modified") applyReactionDoc(change.doc);
        }
      },
      err => console.error("[reactions] snapshot error:", err.code, err.message),
    );
    return () => {
      unsub();
      for (const ids of reactionTimersRef.current.values()) {
        for (const t of ids) window.clearTimeout(t);
      }
      reactionTimersRef.current.clear();
    };
  }, [matchId]);

  // Same rationale as PlayPage: latch dealing before paint so the dealt hand doesn’t flash.
  useLayoutEffect(() => {
    if (!match) return;
    const p = match.public;
    const isFreshHand =
      p.phase === "bidding" && p.bids.length === 0 && p.tricks.length === 0;
    if (isFreshHand && match.handNumber !== lastDealtHandRef.current) {
      lastDealtHandRef.current = match.handNumber;
      setDealingForHand(match.handNumber);
    }
  }, [
    match?.handNumber,
    match?.public.phase,
    match?.public.bids.length,
    match?.public.tricks.length,
  ]);

  // Drop any previous hand’s cards as soon as the server advances handNumber into a new deal,
  // so we never briefly pair old private cards with the new public hand (and we can trust
  // “length matches handSizes” for ending the deal overlay).
  useLayoutEffect(() => {
    if (!match) return;
    const curr = match.handNumber;
    const prev = prevHydrateHandNumRef.current;
    const p = match.public;
    const isFreshHand =
      p.phase === "bidding" && p.bids.length === 0 && p.tricks.length === 0;
    if (isFreshHand && prev !== null && curr !== prev) {
      // If Firestore delivers our private `/privateHands` update before the top-level match
      // doc, we may already be showing the new six — clearing here would strand `myCards === null`
      // because the private listener does not fire again.
      if (lastPrivateDocHandNumberRef.current !== curr) {
        setMyCards(null);
      }
    }
    prevHydrateHandNumRef.current = curr;
  }, [
    match?.handNumber,
    match?.public.phase,
    match?.public.bids.length,
    match?.public.tricks.length,
  ]);

  useEffect(() => {
    if (!match) return;
    const isFreshHand =
      match.public.phase === "bidding" &&
      match.public.bids.length === 0 &&
      match.public.tricks.length === 0;
    if (!isFreshHand) return;
    handStartMsRef.current = performance.now();
    loggedReadyHandRef.current = null;
    if (perfDebugEnabled()) {
      console.debug("[perf][deal] new hand started", {
        matchId,
        handNumber: match.handNumber,
      });
    }
  }, [matchId, match?.handNumber, match?.public.phase, match?.public.bids.length, match?.public.tricks.length]);
  // First render after Firebase advances `handNumber` still has dealingForHand === null until
  // the layout effect runs; OR with ref catches that frame so the fan never flashes (group play).
  const pubForDeal = match?.public;
  const isFreshBiddingHand =
    !!pubForDeal &&
    pubForDeal.phase === "bidding" &&
    pubForDeal.bids.length === 0 &&
    pubForDeal.tricks.length === 0;
  const isDealing =
    !!match &&
    isFreshBiddingHand &&
    (dealingForHand === match.handNumber ||
      match.handNumber !== lastDealtHandRef.current);

  const deckHydrating = useMemo(() => {
    if (!match || mySeat === null || myCards !== null) return false;
    const expected = match.public.handSizes[mySeat];
    return expected > 0;
  }, [match, mySeat, myCards]);

  // ---- Auth/guard rails ------------------------------------
  if (!user) return <Navigate to="/" replace />;
  if (!profile) return <Navigate to="/" replace />;
  if (notFound) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="bg-white/5 rounded-2xl p-8 max-w-md w-full text-center">
          <h1 className="text-2xl font-bold mb-2">Match not found</h1>
          <button
            onClick={() => navigate("/", { replace: true })}
            className="mt-4 px-4 py-2 rounded-lg bg-white text-slate-900 font-semibold"
          >
            Home
          </button>
        </div>
      </div>
    );
  }
  if (!match) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="bg-white/5 rounded-2xl p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-semibold mb-2">Loading match…</h1>
          {error ? (
            <>
              <div className="text-red-300 text-sm mb-4 break-words">{error}</div>
              <div className="text-left text-xs text-white/50 font-mono bg-black/30 rounded p-3 mb-4 break-all">
                <div>uid: {user.uid}</div>
                <div>match: {matchId}</div>
                <div>signed in as: {user.isAnonymous ? "guest" : user.email ?? user.displayName ?? "—"}</div>
              </div>
              <button
                onClick={() => navigate("/", { replace: true })}
                className="px-4 py-2 rounded-lg bg-white text-slate-900 font-semibold"
              >
                Back to home
              </button>
            </>
          ) : (
            <div className="text-white/50 text-sm">Connecting to the table…</div>
          )}
        </div>
      </div>
    );
  }
  if (mySeat === null) {
    return (
      <div className="px-6 py-10 text-center text-white/70">
        You're not a player in this match.
      </div>
    );
  }

  // Fix table orientation so the current player always sees themselves at the
  // bottom. Visually: me = 0-offset, left opponent = me+1, partner = me+2,
  // right opponent = me+3.
  const view = (s: SeatIndex): SeatIndex => (((s - mySeat + 4) % 4) as SeatIndex);
  const seatAtView: Record<SeatIndex, SeatIndex> = {
    0: mySeat,
    1: ((mySeat + 1) % 4) as SeatIndex,
    2: ((mySeat + 2) % 4) as SeatIndex,
    3: ((mySeat + 3) % 4) as SeatIndex,
  };

  const hand = match.public;
  const myTeam: TeamId = TEAM_OF[mySeat];
  const winnerTeam = projectedWinnerForMatch(match);
  /** Rematch callables require this field; projected scores can flip UI early. */
  const matchWinnerCommitted = match.winner != null;
  const boardScores = match.winner
    ? { scoreA: match.scoreA, scoreB: match.scoreB }
    : projectedRunningTotals(match.scoreA, match.scoreB, match.public, match.ruleset);
  const myTeamScore = myTeam === "A" ? boardScores.scoreA : boardScores.scoreB;
  const oppTeamScore = myTeam === "A" ? boardScores.scoreB : boardScores.scoreA;

  // Is it my turn?
  const myTurn =
    (hand.phase === "bidding" && hand.turn === mySeat) ||
    (hand.phase === "choosingTrump" && hand.bidder === mySeat) ||
    (hand.phase === "playing" && hand.turn === mySeat);

  // ---- Action handlers -------------------------------------
  async function doBid(value: BidValue | "pass") {
    if (!matchId) return;
    setBusy(true); setError(null);
    try { await callSubmitBid(matchId, value); }
    catch (e: any) { setError(e?.message ?? "Failed to submit bid."); }
    finally { setBusy(false); }
  }
  async function doTrump(suit: Suit) {
    if (!matchId) return;
    setBusy(true); setError(null);
    try { await callSubmitTrumpChoice(matchId, suit); }
    catch (e: any) { setError(e?.message ?? "Failed to pick trump."); }
    finally { setBusy(false); }
  }
  async function doPlay(card: Card) {
    if (!matchId) return;
    setBusy(true); setError(null);
    try { await callSubmitPlay(matchId, card); }
    catch (e: any) { setError(e?.message ?? "Failed to play card."); }
    finally { setBusy(false); }
  }
  async function doClaimCoke() {
    if (!matchId) return;
    setBusy(true); setError(null);
    try { await callClaimCoke(matchId); }
    catch (e: any) { setError(e?.message ?? "Failed to claim coke hand."); }
    finally { setBusy(false); }
  }
  async function doNext() {
    if (!matchId) return;
    setBusy(true); setError(null);
    try { await callDealNextHand(matchId); }
    catch (e: any) { setError(e?.message ?? "Failed to deal next hand."); }
    finally { setBusy(false); }
  }

  async function doRematchSameTable() {
    if (!matchId) return;
    setRematchBusy(true);
    setError(null);
    try {
      const { matchId: nextId } = await callStartRematch(matchId);
      navigate(`/play-online/${nextId}`, { replace: true });
    } catch (e: any) {
      setError(e?.message ?? "Couldn't start a rematch.");
    } finally {
      setRematchBusy(false);
    }
  }

  async function sendReaction(emoji: ReactionEmoji) {
    if (!matchId || !user || mySeat === null) return;
    if (Date.now() < nextReactionAt) return;
    setNextReactionAt(Date.now() + REACTION_COOLDOWN_MS);
    try {
      const docRef = await addDoc(collection(db, "matches", matchId, "reactions"), {
        uid: user.uid,
        seat: mySeat,
        emoji,
        createdAt: serverTimestamp(),
      });
      console.log("[reactions] (1) Firestore write ok", {
        docId: docRef.id,
        matchId,
        seat: mySeat,
        emoji,
        uid: user.uid,
      });
    } catch (e) {
      console.error("reaction write failed", e);
    }
  }

  const nameForSeat = (s: SeatIndex) => {
    if (s === mySeat) return "You";
    const n = match.seats[s]?.screenName?.trim();
    return n || `Seat ${s}`;
  };
  const phaseLabel = ({
    deal: "Dealing", bidding: "Bidding", choosingTrump: "Choosing trump",
    playing: "Playing", scoring: "Scoring", done: "Hand complete",
  })[hand.phase];

  // Whose action are we waiting on?
  const actorSeat = currentActor(hand);
  const waitingNote = (() => {
    if (myTurn || hand.phase === "done" || winnerTeam) return null;
    if (actorSeat === null) return null;
    const isBot = !!match.seats[actorSeat]?.botDifficulty;
    return isBot
      ? `${nameForSeat(actorSeat)} is thinking…`
      : `Waiting for ${nameForSeat(actorSeat)}…`;
  })();

  const rulesetShort = RULESET_INFO[match.ruleset ?? DEFAULT_RULESET].shortLabel;

  return (
    <div className="max-w-5xl mx-auto px-4 py-3 sm:py-6">
      <div className="mb-2 sm:mb-3">
        <div className="flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <ScoreBadge label="You" score={myTeamScore} highlight />
            <ScoreBadge label="Opp." score={oppTeamScore} />
          </div>
          <div className="shrink-0">
            <button
              type="button"
              onClick={() => navigate("/", { replace: true })}
              className="text-sm text-white/60 hover:text-white border border-white/15 rounded-lg px-3 py-1.5"
            >
              Exit
            </button>
          </div>
        </div>
        <div className="mt-1.5 text-[10px] sm:text-xs text-white/60 text-center sm:text-left leading-snug">
          <span>
            Hand {match.handNumber} · {phaseLabel}
          </span>
          <span className="text-[9px] sm:text-[10px] uppercase tracking-wide text-white/35 ml-1.5">
            {rulesetShort}
          </span>
          {!hand.trump ? (
            <>
              <span className="mx-1.5 text-white/25">·</span>
              <span className="text-white/40">Trump: —</span>
            </>
          ) : null}
          {hand.bid && hand.bidder !== undefined && (
            <>
              {" "}
              <span className="mx-1.5 text-white/25">·</span>
              Bid {hand.bid} · {nameForSeat(hand.bidder)}
            </>
          )}
        </div>
      </div>

      <div className="relative">
        <div className="relative mb-1 sm:mb-1.5 px-1 sm:px-0">
          <div className="flex justify-center">
            <SeatCard
              seat={seatAtView[2]}
              label="Partner"
              occupant={match.seats[seatAtView[2]]}
              handSize={hand.handSizes[seatAtView[2]]}
              active={actorSeat === seatAtView[2]}
              dealer={hand.dealer === seatAtView[2]}
            reaction={seatReactions[seatAtView[2]]}
              compact
              className="max-w-[10rem] w-full shrink-0"
            />
          </div>
          {hand.trump ? (
            <div className="absolute right-0 top-0 flex flex-col items-center leading-none pointer-events-none select-none sm:right-1">
              <span className="text-[8px] uppercase tracking-wide text-white/40 mb-0.5">Trump</span>
              <TrumpSuitHero suit={hand.trump} />
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-[minmax(0,4.75rem)_minmax(0,1fr)_minmax(0,4.75rem)] sm:grid-cols-[minmax(0,5.5rem)_2fr_minmax(0,5.5rem)] gap-2 sm:gap-4 items-center mt-1">
          <SeatCard
            seat={seatAtView[1]}
            label="Opp. (L)"
            occupant={match.seats[seatAtView[1]]}
            handSize={hand.handSizes[seatAtView[1]]}
            active={actorSeat === seatAtView[1]}
            dealer={hand.dealer === seatAtView[1]}
            reaction={seatReactions[seatAtView[1]]}
            narrow
          />
          <CenterTable
            hand={hand}
            handNumber={match.handNumber}
            frozenTrick={frozenTrick}
            trickCardsVisible={trickCardsVisible}
            nameFor={nameForSeat}
            viewOf={view}
            myTeam={myTeam}
            matchBanner={
              winnerTeam
                ? winnerTeam === myTeam
                  ? "Your team wins!"
                  : "Your team lost."
                : undefined
            }
          />
          <SeatCard
            seat={seatAtView[3]}
            label="Opp. (R)"
            occupant={match.seats[seatAtView[3]]}
            handSize={hand.handSizes[seatAtView[3]]}
            active={actorSeat === seatAtView[3]}
            dealer={hand.dealer === seatAtView[3]}
            reaction={seatReactions[seatAtView[3]]}
            narrow
          />
        </div>

        {hand.phase === "bidding" && hand.turn === mySeat && !winnerTeam && (
          <div className="mt-2">
            <BiddingPanel
              bids={hand.bids}
              onBid={doBid}
              onClaimCoke={doClaimCoke}
              canCoke={
                (match.ruleset ?? DEFAULT_RULESET) === "vacation" &&
                !!myCards &&
                isCokeHand(myCards) &&
                !(hand.cokeClaimed ?? []).includes(mySeat)
              }
              disabled={busy}
              compact
            />
          </div>
        )}
        {hand.phase === "choosingTrump" && hand.bidder === mySeat && !winnerTeam && (
          <div className="mt-2">
            <TrumpPanel onPick={doTrump} disabled={busy} compact />
          </div>
        )}
        {hand.phase === "done" && !winnerTeam && (
          <div className="mt-2">
            <HandScorePanel hand={hand} onNext={doNext} disabled={busy} myTeam={myTeam} />
          </div>
        )}

        <div className="mt-2 sm:mt-3">
          {seatReactions[mySeat] ? (
            <div className="mb-2 flex justify-center">
              <ReactionBubble reaction={seatReactions[mySeat]!} />
            </div>
          ) : null}
          <YourHand
            cards={myCards ?? []}
            awaitingDeck={deckHydrating}
            legal={legalMyPlays}
            onPlay={doPlay}
            disabled={busy}
            isMyTurn={myTurn}
            phase={hand.phase}
            replayFromLog={match?.eventLog}
            replaySeat={mySeat}
          />
        </div>
        <div className="mt-2 flex justify-center">
          <div className="rounded-full border border-white/15 bg-black/20 px-2 py-1 backdrop-blur-sm">
            <div className="flex items-center gap-1">
              {REACTION_EMOJIS.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => void sendReaction(emoji)}
                  disabled={nowMs < nextReactionAt || !user || mySeat === null}
                  className="h-8 w-8 rounded-full text-base transition hover:bg-white/15 disabled:opacity-40"
                  title={`React ${emoji}`}
                  aria-label={`React ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-1 h-4 text-center text-[10px] text-white/45">
          {nowMs < nextReactionAt
            ? `Reaction cooldown ${Math.ceil((nextReactionAt - nowMs) / 1000)}s`
            : ""}
        </div>

        {isDealing && (
          <DealAnimation
            dealerSlot={view(hand.dealer)}
            onDone={() => setDealingForHand(null)}
          />
        )}
      </div>

      {waitingNote && (
        <div className="mt-2 text-sm text-white/50 text-center">{waitingNote}</div>
      )}
      {nudgeStatus && (
        <div className="mt-2 text-sm text-amber-300 text-center">{nudgeStatus}</div>
      )}

      {winnerTeam && (
        <div className="mt-2 sm:mt-3">
          <GameOverPanel
            tableRoomLoaded={tableRoom !== undefined}
            hostCanRematch={
              tableRoom !== undefined && user?.uid !== undefined && tableRoom.hostUid === user.uid
            }
            rematchEnabled={matchWinnerCommitted}
            hostLeftWithoutRematch={
              tableRoom !== undefined && tableRoom.status === "closed" && !tableRoom.rematchMatchId
            }
            rematchBusy={rematchBusy}
            onRematch={doRematchSameTable}
            onHome={() => navigate("/", { replace: true })}
          />
        </div>
      )}

      {error && <div className="mt-4 text-red-300 text-sm">{error}</div>}
    </div>
  );
}

// ============================================================
// Sub-components (most are local variants of PlayPage's — kept
// simple; can be unified in a future pass.)
// ============================================================

function SeatCard({
  seat, label, occupant, handSize, active, dealer, reaction, className = "", compact, narrow,
}: {
  seat: SeatIndex;
  label: string;
  occupant: SeatOccupant | null;
  handSize: number;
  active: boolean;
  dealer?: boolean;
  reaction?: SeatReaction;
  className?: string;
  compact?: boolean;
  narrow?: boolean;
}) {
  void seat;
  const isBot = !!occupant?.botDifficulty;
  const baseName = occupant?.screenName ?? "—";
  const displayName = isBot ? `${baseName} (bot)` : baseName;
  const pad = compact ? "px-2 py-1" : narrow ? "px-1.5 py-1.5" : "px-3 py-2";
  const labelCls = compact || narrow ? "text-[10px]" : "text-xs";
  return (
    <div
      className={
        "relative overflow-visible rounded-lg text-sm text-center border transition-shadow w-full " +
        (reaction ? "z-[50] isolate " : "") +
        pad + " " +
        (active
          ? "bg-emerald-500/20 border-emerald-400/60 text-white " +
            "shadow-[0_0_18px_rgba(52,211,153,0.45)] animate-[pulse_2s_ease-in-out_infinite]"
          : "bg-white/5 border-white/10 text-white/70") +
        (className ? " " + className : "")
      }
    >
      {reaction ? (
        <ReactionBubble
          reaction={reaction}
          className="absolute -top-[5.25rem] left-1/2 -translate-x-1/2 w-max max-w-[min(100vw-2rem,22rem)] flex justify-center"
        />
      ) : null}
      <div className={"uppercase tracking-wide text-white/40 " + labelCls}>{label}</div>
      <div className="font-semibold truncate text-xs leading-tight">{displayName}</div>
      <div className="mt-0.5 flex items-center justify-center gap-1 flex-wrap">
        {dealer && (
          <span className="text-[9px] sm:text-[10px] font-semibold px-1.5 sm:px-2 py-0.5 rounded-full border border-amber-300/60 bg-amber-400/20 text-amber-100">
            Dealer
          </span>
        )}
        {active && (
          <span className="text-[9px] sm:text-[10px] font-semibold px-1.5 sm:px-2 py-0.5 rounded-full border border-emerald-300/60 bg-emerald-400/20 text-emerald-100">
            Next
          </span>
        )}
      </div>
      <div className={"text-[10px] text-white/40 " + (compact ? "mt-0.5" : "mt-1")}>
        {handSize} cards
      </div>
    </div>
  );
}

function ReactionBubble({
  reaction,
  className = "",
}: {
  reaction: SeatReaction;
  className?: string;
}) {
  const fadeSec = REACTION_FADE_MS / 1000;

  useEffect(() => {
    console.log("[reactions] (3) ReactionBubble render/mount pulse", {
      docId: reaction.id,
      emoji: reaction.emoji,
      fading: reaction.fading,
    });
  }, [reaction.id, reaction.emoji, reaction.fading]);

  return (
    <motion.div
      layout={false}
      key={reaction.id}
      initial={{ opacity: 0, y: 18, scale: 0.55 }}
      animate={
        reaction.fading
          ? { opacity: 0, y: -52, scale: 0.82 }
          : { opacity: 1, y: 0, scale: 1 }
      }
      transition={
        reaction.fading
          ? { duration: fadeSec, ease: "easeIn" }
          : { type: "spring", stiffness: 420, damping: 24, mass: 0.72 }
      }
      className={
        "pointer-events-none select-none z-[120] rounded-[1.35rem] border-[3px] border-amber-400/90 " +
        "bg-gradient-to-b from-amber-950/95 to-neutral-950/95 px-6 py-4 " +
        "text-5xl sm:text-6xl leading-none shadow-[0_0_42px_rgba(251,191,36,0.55),0_12px_40px_rgba(0,0,0,0.55)] " +
        "ring-4 ring-amber-300/40 " +
        (className ? `${className}` : "")
      }
    >
      <span
        className="block text-center [text-shadow:0_3px_12px_rgba(0,0,0,0.9)] drop-shadow-[0_2px_8px_rgba(0,0,0,0.75)]"
        aria-hidden
      >
        {reaction.emoji}
      </span>
    </motion.div>
  );
}

function CenterTable({
  hand, handNumber, frozenTrick, trickCardsVisible, nameFor, viewOf, myTeam, matchBanner,
}: {
  hand: PublicHandState;
  handNumber: number;
  frozenTrick: Trick | null;
  trickCardsVisible: boolean;
  nameFor: (s: SeatIndex) => string;
  viewOf: (s: SeatIndex) => SeatIndex;
  myTeam: TeamId;
  matchBanner?: string;
}) {
  // "You"/"Opp." labels relative to the local player, used for the
  // end-of-hand result panel.
  const labelTeamForMe = (t?: TeamId) =>
    t === undefined ? "—" : t === myTeam ? "You" : "Opp.";
  const displayTrick = frozenTrick ?? hand.currentTrick;
  const plays = displayTrick?.plays ?? [];
  const winnerSeat = frozenTrick?.winner;
  const hideFrozenCards = frozenTrick && !trickCardsVisible;

  const inBidding = hand.phase === "bidding";
  const bidBySeat: Partial<Record<SeatIndex, BidValue | "pass">> = {};
  for (const b of hand.bids) bidBySeat[b.seat] = b.value;
  const highBid = hand.bids.reduce<{ seat: SeatIndex; value: BidValue } | null>((best, b) => {
    if (b.value === "pass") return best;
    const v = b.value as BidValue;
    if (!best || v > best.value) return { seat: b.seat, value: v };
    return best;
  }, null);

  const cokedSeats = new Set(hand.cokeClaimed ?? []);
  // Map each screen-slot view (0=bottom/me, 1=left, 2=top, 3=right) to a
  // direction so cards slide in from the playing seat's position.
  const slotDir: Record<SeatIndex, "top" | "bottom" | "left" | "right"> = {
    0: "bottom",
    1: "left",
    2: "top",
    3: "right",
  };
  const winnerDir =
    winnerSeat !== undefined ? slotDir[viewOf(winnerSeat)] : undefined;

  const seatAtSlot = (slot: SeatIndex): SeatIndex => {
    for (let s = 0; s < 4; s++) {
      if (viewOf(s as SeatIndex) === slot) return s as SeatIndex;
    }
    throw new Error("unreachable");
  };
  const meSeat = seatAtSlot(0);

  const renderBid = (s: SeatIndex) => {
    const showDealerHere = hand.dealer === s && s === meSeat;
    return (
      <BidPill
        bid={bidBySeat[s]}
        tag={nameFor(s)}
        coked={cokedSeats.has(s)}
        showDealer={showDealerHere}
      />
    );
  };

  const centerLabel = (() => {
    if (frozenTrick) {
      const name = winnerSeat !== undefined ? nameFor(winnerSeat) : "";
      return (
        <div className="text-center">
          <div className="text-[10px] text-emerald-200 uppercase tracking-wide">Trick won by</div>
          <div className="text-emerald-300 font-bold text-sm leading-tight">{name}</div>
        </div>
      );
    }
    if (hand.phase === "playing") {
      return <div className="text-white/30 text-xs uppercase">Trick {hand.tricks.length + 1}/6</div>;
    }
    if (hand.phase === "bidding") {
      return (
        <div className="text-center">
          {highBid ? (
            <>
              <div className="text-[10px] text-white/40 uppercase tracking-wide">High bid</div>
              <div className="text-2xl font-bold text-emerald-300 leading-none">{highBid.value}</div>
              <div className="text-[10px] text-white/50 mt-0.5">by {nameFor(highBid.seat)}</div>
            </>
          ) : (
            <div className="text-white/30 text-xs uppercase">Bidding…</div>
          )}
        </div>
      );
    }
    if (hand.phase === "choosingTrump") {
      return <div className="text-white/30 text-xs uppercase text-center">Choosing trump…</div>;
    }
    return null;
  })();

  // Once the hand is over (and any trick-freeze has already played out),
  // replace the 3×3 seat grid with the hand-result summary.
  const showHandResult = !frozenTrick && hand.phase === "done";
  const bidderLabel =
    hand.bidder !== undefined ? nameFor(hand.bidder) : undefined;

  const tableBox =
    "min-h-[min(400px,52svh)] max-h-[min(520px,62svh)] w-full bg-white/5 rounded-2xl border border-white/10 p-3 sm:p-4 relative overflow-visible flex flex-col " +
    "min-[768px]:max-[1535px]:min-h-[min(480px,60svh)] min-[768px]:max-[1535px]:max-h-[min(680px,72svh)] " +
    "min-[768px]:max-[1535px]:p-5";

  return (
    <div className={tableBox}>
      {matchBanner ? (
        <div className="shrink-0 text-center pb-2 sm:pb-3 border-b border-white/10 mb-2">
          <div className="text-3xl font-bold">{matchBanner}</div>
        </div>
      ) : null}
      <div className="flex-1 min-h-0 flex flex-col items-stretch justify-start w-full">
        {showHandResult ? (
          <div className="flex-1 flex flex-col justify-center min-h-0 w-full">
            <HandResultCenter
              handNumber={handNumber}
              bid={hand.bid}
              bidderLabel={bidderLabel}
              score={hand.score}
              abandoned={hand.abandoned}
              labelTeam={labelTeamForMe}
            />
          </div>
        ) : inBidding ? (
          <FourSeatTrickLayout
            top={renderBid(seatAtSlot(2))}
            left={renderBid(seatAtSlot(1))}
            center={centerLabel}
            right={renderBid(seatAtSlot(3))}
            bottom={renderBid(seatAtSlot(0))}
          />
        ) : hand.phase === "choosingTrump" ? (
          <div className="flex flex-1 flex-col items-center justify-center w-full min-h-[min(260px,38svh)] min-[768px]:max-[1535px]:min-h-[min(340px,44svh)] px-2">
            {centerLabel}
          </div>
        ) : (
          <TrickPlaysAtSeats
            centerLabel={centerLabel}
            plays={plays}
            hideFrozenCards={!!hideFrozenCards}
            winnerSeat={winnerSeat}
            winnerDir={winnerDir}
            seatDir={s => slotDir[viewOf(s)]}
            nameFor={nameFor}
            showDealer={s => hand.dealer === s && s === meSeat}
          />
        )}
      </div>
    </div>
  );
}

function YourHand({
  cards,
  awaitingDeck = false,
  legal,
  onPlay,
  disabled,
  isMyTurn,
  phase,
  replayFromLog,
  replaySeat,
}: {
  cards: Card[];
  /** Fresh deal: private hand snapshot not applied yet (avoid stale card flash). */
  awaitingDeck?: boolean;
  legal: Card[];
  onPlay: (c: Card) => void;
  disabled: boolean;
  isMyTurn: boolean;
  phase: Phase;
  /** Match-level log (online); used to repaint your six cards between hands. */
  replayFromLog?: GameEvent[];
  replaySeat?: SeatIndex | null;
}) {
  const sorted = useMemo(
    () =>
      [...cards].sort((a, b) => {
        if (a.suit !== b.suit) return a.suit.localeCompare(b.suit);
        return a.rank - b.rank;
      }),
    [cards],
  );

  const replayFan = useMemo(() => {
    if (phase !== "done" || sorted.length > 0) return [];
    if (replaySeat == null || !replayFromLog?.length) return [];
    const fromLog = cardsPlayedFromLogForSeat(replayFromLog, replaySeat);
    return [...fromLog].sort((a, b) => {
      if (a.suit !== b.suit) return a.suit.localeCompare(b.suit);
      return a.rank - b.rank;
    });
  }, [phase, sorted.length, replayFromLog, replaySeat]);

  const fanCards = sorted.length > 0 ? sorted : replayFan;
  const showReplayHint = sorted.length === 0 && replayFan.length > 0 && phase === "done";

  const legalKey = new Set(legal.map(c => `${c.suit}${c.rank}`));
  const isPlayingPhase = legal.length > 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wide text-white/40">
          Your hand ({fanCards.length})
        </div>
        <div className="flex items-center gap-2">
          {isMyTurn && <TurnChip label="Your turn · next to act" />}
        </div>
      </div>
      {fanCards.length > 0 ? (
        <>
          {showReplayHint ? (
            <div className="text-[10px] text-white/45 uppercase tracking-wide mb-1">
              Your cards this hand
            </div>
          ) : null}
          <HandFan
            cards={fanCards}
            legalKeys={legalKey}
            onPlay={onPlay}
            isPlayingPhase={isPlayingPhase}
            disabled={disabled}
          />
        </>
      ) : awaitingDeck ? (
        <div className="rounded-lg border border-dashed border-emerald-400/35 bg-emerald-950/20 px-4 py-6 text-center text-emerald-100/90 text-sm font-medium">
          Receiving your cards…
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-white/15 px-4 py-5 text-center text-white/45 text-sm">
          {phase === "done"
            ? "Hand complete — your cards refresh on the next deal."
            : "No cards left this hand."}
        </div>
      )}
    </div>
  );
}

function BiddingPanel({
  bids, onBid, onClaimCoke, canCoke, disabled, compact,
}: {
  bids: Bid[];
  onBid: (v: BidValue | "pass") => void;
  onClaimCoke: () => void;
  canCoke: boolean;
  disabled: boolean;
  compact?: boolean;
}) {
  const highest = bids.reduce((h, b) => (b.value !== "pass" && b.value > h ? (b.value as number) : h), 1);
  const options: (BidValue | "pass")[] = ["pass"];
  for (const v of [2, 3, 4] as BidValue[]) if (v > highest) options.push(v);
  const pad = compact ? "p-2.5 sm:p-4" : "p-3 sm:p-4";
  const titleMb = compact ? "mb-1.5 sm:mb-3" : "mb-2 sm:mb-3";
  return (
    <div className={"bg-white/5 border border-white/10 rounded-xl " + pad}>
      <div className={"text-xs sm:text-sm font-medium text-white/90 " + titleMb}>Your bid:</div>
      <div className="flex gap-1.5 flex-wrap">
        {options.map(opt => (
          <button
            key={opt}
            disabled={disabled}
            onClick={() => onBid(opt)}
            className={
              "px-4 py-2 rounded-lg font-semibold disabled:opacity-50 " +
              (opt === "pass"
                ? "bg-white/10 hover:bg-white/20 text-white"
                : "bg-emerald-500 hover:bg-emerald-400 text-slate-900")
            }
          >
            {opt === "pass" ? "Pass" : `Bid ${opt}`}
          </button>
        ))}
        {canCoke && (
          <button
            disabled={disabled}
            onClick={onClaimCoke}
            className="px-4 py-2 rounded-lg font-semibold bg-amber-400 hover:bg-amber-300 text-slate-900 disabled:opacity-50"
            title="Your hand is all 3–9s. Take 6 new cards and sit out the bid."
          >
            Claim coke hand
          </button>
        )}
      </div>
    </div>
  );
}

function TrumpPanel({
  onPick, disabled, compact,
}: { onPick: (s: Suit) => void; disabled: boolean; compact?: boolean }) {
  const pad = compact ? "p-2.5 sm:p-4" : "p-3 sm:p-4";
  const titleMb = compact ? "mb-1.5 sm:mb-3" : "mb-2 sm:mb-3";
  const btnY = compact ? "py-1.5 sm:py-2" : "py-2";
  return (
    <div className={"bg-white/5 border border-white/10 rounded-xl " + pad}>
      <div className={"text-xs sm:text-sm font-medium text-white/90 " + titleMb}>
        You won the bid — choose trump:
      </div>
      <div className="grid grid-cols-4 gap-1 sm:gap-1.5">
        {(["C", "D", "H", "S"] as Suit[]).map(s => (
          <button
            key={s}
            type="button"
            disabled={disabled}
            onClick={() => onPick(s)}
            className={
              "min-w-0 flex flex-col items-center justify-center rounded-lg px-0.5 sm:px-3 font-semibold " +
              btnY +
              " disabled:opacity-50 text-[11px] sm:text-sm leading-tight transition-colors " +
              TRUMP_CHOICE_BUTTON_CLASS[s]
            }
          >
            <span className="text-lg sm:text-xl leading-none">{SUIT_SYMBOL[s]}</span>
            <span className="mt-0.5 truncate max-w-full">{SUIT_LABEL[s]}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function HandScorePanel({
  hand, onNext, disabled, myTeam,
}: {
  hand: PublicHandState;
  onNext: () => void;
  disabled: boolean;
  myTeam: TeamId;
}) {
  // The scoring breakdown now lives in the center of the table (see
  // CenterTable → HandResultCenter). This panel just surfaces the
  // bidder-set warning (if any) and the Deal-next-hand CTA.
  const setBack =
    !!hand.score && !hand.score.bidderMade && hand.bidder !== undefined && hand.bid;
  const bidderTeamLabel =
    hand.bidder !== undefined && TEAM_OF[hand.bidder] === myTeam ? "Your team" : "Opponents";

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between gap-4">
      <div className="text-sm text-white/70">
        {hand.abandoned ? (
          <>Hand abandoned — all passed or coked.</>
        ) : setBack ? (
          <span className="text-red-300">
            {bidderTeamLabel} was set back — lost {hand.bid} points.
          </span>
        ) : (
          <>Hand complete.</>
        )}
      </div>
      <button
        disabled={disabled}
        onClick={onNext}
        className="px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold disabled:opacity-50"
      >
        Deal next hand →
      </button>
    </div>
  );
}

function GameOverPanel({
  tableRoomLoaded,
  hostCanRematch,
  rematchEnabled,
  hostLeftWithoutRematch,
  rematchBusy,
  onRematch,
  onHome,
}: {
  tableRoomLoaded: boolean;
  hostCanRematch: boolean;
  /** True once Firestore `match.winner` is set (server accepted the rubber end). */
  rematchEnabled: boolean;
  hostLeftWithoutRematch: boolean;
  rematchBusy: boolean;
  onRematch: () => void;
  onHome: () => void;
}) {
  const anyBusy = rematchBusy;
  const hostWaitingForWinnerDoc = hostCanRematch && !rematchEnabled;
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
      <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center items-stretch">
        {hostCanRematch ? (
          <button
            type="button"
            disabled={anyBusy || !rematchEnabled}
            onClick={onRematch}
            className="px-5 py-2.5 rounded-lg bg-emerald-500 text-slate-900 font-semibold hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {rematchBusy
              ? "Starting rematch…"
              : hostWaitingForWinnerDoc
                ? "Preparing rematch…"
                : "Rematch"}
          </button>
        ) : (
          <div className="px-5 py-2.5 rounded-lg bg-white/10 text-white/80 text-sm">
            Waiting for rematch...
          </div>
        )}
        <button
          type="button"
          disabled={anyBusy}
          onClick={onHome}
          className="px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white disabled:opacity-50"
        >
          Return to Lobby
        </button>
      </div>
      {tableRoomLoaded && !hostCanRematch && !hostLeftWithoutRematch ? (
        <p className="mt-3 text-[11px] text-white/45">Host can start a rematch; you'll be moved automatically.</p>
      ) : null}
      {tableRoomLoaded && hostLeftWithoutRematch ? (
        <p className="mt-3 text-[11px] text-white/45">Host left the table.</p>
      ) : null}
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================

function currentActor(hand: PublicHandState): SeatIndex | null {
  if (hand.phase === "bidding") return hand.turn ?? null;
  if (hand.phase === "choosingTrump") return hand.bidder ?? null;
  if (hand.phase === "playing") return hand.turn ?? null;
  return null;
}

// Build a HandState from the public projection + the caller's hand, suitable
// for passing to legalPlays() — the engine only inspects hands[actorSeat].
function reconstructHandState(match: MatchDoc, mySeat: SeatIndex, myCards: Card[]): HandState {
  const hands: Card[][] = [[], [], [], []];
  hands[mySeat] = myCards;
  const pub = match.public;
  return {
    handNumber: match.handNumber,
    dealer: pub.dealer,
    hands,
    bids: pub.bids,
    bidder: pub.bidder,
    bid: pub.bid,
    trump: pub.trump,
    tricks: pub.tricks,
    currentTrick: pub.currentTrick,
    turn: pub.turn,
    phase: pub.phase,
    score: pub.score,
    capturedCards: { A: [], B: [] },
    eventLog: [],
  };
}

function projectedWinnerForMatch(match: MatchDoc): TeamId | undefined {
  if (match.winner) return match.winner;
  const hand = match.public;
  if (hand.phase !== "done" || !hand.score || hand.abandoned || hand.bidder === undefined) {
    return undefined;
  }
  const { scoreA, scoreB } = projectedRunningTotals(
    match.scoreA,
    match.scoreB,
    hand,
    match.ruleset,
  );
  const bidderTeam = TEAM_OF[hand.bidder];
  if (scoreA >= 11 && scoreB >= 11) return bidderTeam;
  if (scoreA >= 11) return "A";
  if (scoreB >= 11) return "B";
  return undefined;
}

